<template>
  <div class="flex flex-col">
    <!-- Drag & Drop Area -->
    <div
      class="@apply h-[160px] p-4 grow flex items-center justify-center gap-2.5 rounded-2xl border-[1.5px] border-gray-300 dark:border-neutral-600"
      :class="hoverClass"
      @drop.prevent="drop"
      @dragenter.prevent
      @dragover.prevent="hoverClass = 'border-solid border-blue-400 bg-opacity-50 bg-gradient-to-b from-blue-50 to-blue-100 dark:border-blue-800 dark:bg-opacity-25 dark:from-neutral-950 dark:to-blue-950'" 
      @dragleave.prevent="hoverClass = 'border-dashed'"
    >
    <!-- bg-blue-100 bg-opacity-70 dark:bg-blue-900 dark:bg-opacity-70 -->
      <div class="flex flex-col items-center gap-3">
        <div class="ring-1 ring-gray-400 dark:ring-neutral-600 rounded-lg p-1.5 shadow-lg bg-white dark:bg-neutral-700">
          <CloudArrowUpIcon class="w-6 h-6 text-gray-900 dark:text-neutral-100 pb-0.5 bounce2" />
        </div>

        <div class="flex flex-col items-center">
          <div class="dark:text-neutral-300 text-center text-sm font-light">
            <b class="cursor-pointer text-blue-600 hover:text-blue-500 dark:text-blue-500 dark:hover:text-blue-600 font-semibold" @click="simulateClick()">Click to upload</b>
            or drag and drop
            <br />
          </div>
          <div class="text-xs font-light dark:text-neutral-300 text-gray-600 text-center">{{ props.accepted_text }}</div>
        </div>
      </div>
      <input id="upload" ref="chooseFile" type="file" class="hidden" multiple @change="fileAddedViaBrowse" />
    </div>

    <!-- Uploaded Files Display -->
    <div class="flex flex-wrap gap-y-5 mt-5 h-fit">
      <DocumentsList
        :documents="dtView"
        :downloadable="props.downloadable"
        :type="props.type"
        :offering_id="props.offering_id"
        @remove="removeFile"
      />
    </div>
  </div>
</template>

<script setup>
// Essentials
import { ref, watch } from "vue";

// Components
import DocumentsList from "@/components/applicationinputs/documents/DocumentsList.vue";
import { CloudArrowUpIcon } from "@heroicons/vue/24/outline";

// Props
const props = defineProps({
  files: { type: Array, required: false, default: () => [] }, // Existing files
  type: { type: String, required: false, default: "" }, // Type of file
  offering_id: { type: String, required: false, default: "" }, // Offering ID
  downloadable: { type: Boolean, required: false, default: false }, // If the file is downloadable
  accepted_text: { type: String, required: false, default: "" }, // Accepted file types
});

// State
const hoverClass = ref("border-dashed");
const dt = ref(new DataTransfer());
const dtView = ref([]);
const chooseFile = ref(null);

// Emits
const emit = defineEmits(["changedFiles"]);

// Lifecycle Hook - Initialize Existing Files
watch(
  () => props.files,
  (newFiles) => {
    // Populate dtView and dt with existing files
    dtView.value = [];
    dt.value = new DataTransfer();

    newFiles.forEach((file) => {
      dtView.value.push({
        name: file.name,
        url: file.url || "", // URL for display (if available)
        important: file.type !== "application/pdf" ? " !important" : "",
        size: file.size || file.metadata.size, // Add size if available
        doc_type: file.metadata?.mimetype || file.doc_type || file.type, // Add type if available
        userFolder: file.userFolder || "",
        accountFolder: file.accountFolder || "",
        partyFolder: file.partyFolder || "",
        created_at: file.created_at || "",
      });

      // Create a mock `File` object for DataTransfer if not already a File
      const fileObj = new File([], file.name, {
        type: file.type || "application/octet-stream",
      });
      dt.value.items.add(fileObj);
    });

    emit("changedFiles", dt.value);
  },
  { immediate: true, deep: true },
);

// Functions
const drop = (e) => {
  hoverClass.value = "border-dashed";
  addFiles(e.dataTransfer.files);
};

const fileAddedViaBrowse = (event) => {
  addFiles(event.target.files);
};

const addFiles = (files) => {
  for (let file of files) {
    // Skip duplicates
    if (dtView.value.some((f) => f.name === file.name)) continue;

    if (!["image/jpeg", "image/png", "application/pdf"].includes(file.type)) {
      alert("Accepted file types are jpg, png, & pdf.");
      return
    } else if (file.size > 100000000) {
      alert("Maximum file size is 100MB.");
      return;
    } else {
      dt.value.items.add(file);

      const url = file.type !== "application/pdf" ? URL.createObjectURL(file) : "";
      const important = file.type !== "application/pdf" ? " !important" : "";
      dtView.value.push({ name: file.name, url, important, size: file.size, doc_type: file.type });
    }
  }
  emit("changedFiles", dt.value);
};

const removeFile = (fileName) => {
  const dtTemp = new DataTransfer();

  for (let file of dt.value.files) {
    if (file.name !== fileName) {
      dtTemp.items.add(file);
    }
  }

  dt.value = dtTemp;

  dtView.value = dtView.value.filter((file) => file.name !== fileName);
  emit("changedFiles", dt.value, fileName);
};

const simulateClick = () => {
  chooseFile.value.click();
};
</script>

<style scoped>
.bounce2 {
  animation: bounce2 1s ease-out;
  animation-iteration-count: 4;
}
@keyframes bounce2 {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-4px);
  }
  60% {
    transform: rotate(3deg);
  }
  61% {
    transform: rotate(-3deg);
  }
}
</style>
